/**
 * API Hook for Photosynthesize Dashboard
 * Handles authentication and API calls to backend
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.photosynthesize.app';

interface APIError {
  message: string;
  statusCode: number;
}

interface Photo {
  photo_id: string;
  filename: string;
  capture_date: string;
  upload_date: string;
  quality_score?: number;
  storage_tier?: 'INSTANT' | 'STANDARD' | 'ARCHIVE';
  quality_issues?: string[];
  status: 'uploaded' | 'flagged_for_edit' | 'culled' | 'edited' | 'published';
  thumbnail_url?: string;
  exif?: {
    camera_model?: string;
    lens_model?: string;
    focal_length?: number;
    aperture?: number;
    shutter_speed?: string;
    iso?: number;
  };
}

interface Burst {
  burst_id: string;
  user_id: string;
  photo_ids: string[];
  photo_count: number;
  start_time: string;
  end_time: string;
  processing_status: 'pending' | 'in_progress' | 'completed';
  quality_scores?: Record<string, any>;
  tier_assignments?: {
    hot: string[];
    warm: string[];
    cold: string[];
  };
  recommendations?: {
    keep: any[];
    review: any[];
    delete: any[];
  };
}

interface Stats {
  total_photos: number;
  by_status: Record<string, number>;
  by_camera: Record<string, number>;
  by_lens: Record<string, number>;
  keeper_rate: number;
}

/**
 * Main API hook
 */
export function useAPI() {
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<APIError | null>(null);

  // Get auth token
  useEffect(() => {
    async function getToken() {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        setToken(idToken || null);
        setLoading(false);
      } catch (err) {
        console.error('Error getting auth token:', err);
        setError({ message: 'Authentication failed', statusCode: 401 });
        setLoading(false);
      }
    }

    getToken();
  }, []);

  // Generic API call function
  const apiCall = useCallback(async <T,>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }, [token]);

  return {
    token,
    loading,
    error,
    apiCall,
  };
}

/**
 * Hook for fetching photos
 */
export function usePhotos(limit: number = 50) {
  const { apiCall, loading: authLoading } = useAPI();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastKey, setLastKey] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchPhotos = useCallback(async (reset: boolean = false) => {
    if (authLoading) {
      console.log('Waiting for authentication...');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        ...(lastKey && !reset ? { lastKey } : {}),
      });

      console.log('Fetching photos:', `/photos?${queryParams}`);
      const data = await apiCall<{ photos: Photo[]; lastKey: string | null }>(
        `/photos?${queryParams}`
      );

      console.log('Received photos:', data.photos.length);

      if (reset) {
        setPhotos(data.photos);
      } else {
        setPhotos(prev => [...prev, ...data.photos]);
      }

      setLastKey(data.lastKey);
      setHasMore(data.lastKey !== null);
    } catch (err: any) {
      console.error('Error fetching photos:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, authLoading, limit, lastKey]);

  useEffect(() => {
    if (!authLoading) {
      console.log('Auth ready, fetching photos...');
      fetchPhotos(true);
    }
  }, [authLoading, fetchPhotos]);

  const loadMore = () => {
    if (!loading && hasMore) {
      fetchPhotos(false);
    }
  };

  const refresh = () => {
    setLastKey(null);
    fetchPhotos(true);
  };

  return {
    photos,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
  };
}

/**
 * Hook for fetching bursts
 */
export function useBursts(limit: number = 50) {
  const { apiCall } = useAPI();
  const [bursts, setBursts] = useState<Burst[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBursts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiCall<{ bursts: Burst[] }>(`/bursts?limit=${limit}`);
      setBursts(data.bursts);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, limit]);

  useEffect(() => {
    fetchBursts();
  }, [fetchBursts]);

  return {
    bursts,
    loading,
    error,
    refresh: fetchBursts,
  };
}

/**
 * Hook for fetching burst details with recommendations
 */
export function useBurstDetails(burstId: string | null) {
  const { apiCall } = useAPI();
  const [burst, setBurst] = useState<Burst | null>(null);
  const [recommendations, setRecommendations] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!burstId) return;

    async function fetchBurstDetails() {
      setLoading(true);
      setError(null);

      try {
        // Fetch burst details
        const burstData = await apiCall<{ burst: Burst }>(`/bursts/${burstId}`);
        setBurst(burstData.burst);

        // Fetch recommendations
        const recsData = await apiCall<{ recommendations: any; summary: any }>(
          `/bursts/${burstId}/recommendations`
        );
        setRecommendations(recsData);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchBurstDetails();
  }, [burstId, apiCall]);

  return {
    burst,
    recommendations,
    loading,
    error,
  };
}

/**
 * Hook for fetching user stats
 */
export function useStats(period: string = '30d') {
  const { apiCall } = useAPI();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiCall<Stats>(`/stats?period=${period}`);
      setStats(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, period]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refresh: fetchStats,
  };
}

/**
 * Hook for updating photo status
 */
export function usePhotoActions() {
  const { apiCall } = useAPI();
  const [updating, setUpdating] = useState(false);

  const updatePhotoStatus = async (photoId: string, status: string) => {
    setUpdating(true);
    try {
      await apiCall(`/photos/${photoId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      return true;
    } catch (err) {
      console.error('Error updating photo:', err);
      return false;
    } finally {
      setUpdating(false);
    }
  };

  const batchUpdatePhotoStatus = async (photoIds: string[], status: string) => {
    setUpdating(true);
    try {
      await apiCall('/photos/batch/status', {
        method: 'POST',
        body: JSON.stringify({ photoIds, status }),
      });
      return true;
    } catch (err) {
      console.error('Error batch updating photos:', err);
      return false;
    } finally {
      setUpdating(false);
    }
  };

  const flagPhoto = (photoId: string) => updatePhotoStatus(photoId, 'flagged_for_edit');
  const cullPhoto = (photoId: string) => updatePhotoStatus(photoId, 'culled');
  const publishPhoto = (photoId: string) => updatePhotoStatus(photoId, 'published');

  return {
    updatePhotoStatus,
    batchUpdatePhotoStatus,
    flagPhoto,
    cullPhoto,
    publishPhoto,
    updating,
  };
}

/**
 * Hook for searching photos
 */
export function usePhotoSearch() {
  const { apiCall } = useAPI();
  const [results, setResults] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async (params: {
    lens?: string;
    camera?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams(
        Object.entries(params).filter(([_, v]) => v !== undefined) as [string, string][]
      );

      const data = await apiCall<{ photos: Photo[] }>(
        `/photos/search?${queryParams}`
      );
      setResults(data.photos);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    results,
    loading,
    error,
    search,
  };
}

/**
 * Aggregated statistics interface
 */
export interface AggregatedStats {
  cameras: Record<string, number>;
  lenses: Record<string, number>;
  apertures: Record<string, number>;
  iso: Record<string, number>;
  focal_lengths: Record<string, number>;
  shutter_speeds: Record<string, number>;
  time_of_day: number[];
  gps_heatmap: Array<{ lat: number; lng: number; count: number }>;
  total_photos: number;
  last_updated: string;
}

/**
 * Hook for fetching aggregated statistics (pre-computed, fast)
 */
export function useAggregatedStats() {
  const { apiCall } = useAPI();
  const [stats, setStats] = useState<AggregatedStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiCall<AggregatedStats>('/stats/aggregated');
      setStats(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refresh: fetchStats,
  };
}
